import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articlesList } from "@/mocks/articles";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) setGuideOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isLight = isHomePage && !scrolled && !mobileOpen;

  const navTextClass = isLight
    ? "text-background-50 hover:text-background-100"
    : "text-foreground-700 hover:text-foreground-950";

  const navBgClass = isLight
    ? "hover:bg-background-50/15"
    : "hover:bg-background-100";

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || !isHomePage
          ? "bg-background-50/95 backdrop-blur-md"
          : "bg-gradient-to-b from-black/30 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <a
            href="/"
            className="flex items-center gap-2 no-underline"
          >
            <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight transition-colors ${isLight ? "text-background-50" : "text-foreground-950"}`}>
              Hoobuy<span className={isLight ? "text-primary-300" : "text-primary-500"}>.</span>Lifestyle
            </span>
          </a>

          <div className="hidden md:flex items-center gap-1">
            <a
              href="/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${navTextClass} ${navBgClass}`}
            >
              <i className="ri-home-4-line mr-1.5"></i>
              {t("nav.home")}
            </a>

            <div className="relative" ref={catRef}>
              <button
                onClick={() => { setCatOpen(!catOpen); setGuideOpen(false); }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${navTextClass} ${navBgClass}`}
              >
                {t("nav.categories")}
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
              </button>
              {catOpen && (
                <div className={`absolute top-full left-0 mt-1 w-64 rounded-xl border shadow-lg py-2 max-h-80 overflow-y-auto z-50 ${
                  scrolled || !isHomePage
                    ? "bg-background-50 border-background-200/70"
                    : "bg-background-50/95 backdrop-blur-md border-background-200/50"
                }`}>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setCatOpen(false)}
                    >
                      <span className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${getCategoryIcon(cat.name)} text-primary-600 text-xs`}></i>
                      </span>
                      {cat.namePt}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={guideRef}>
              <button
                onClick={() => { setGuideOpen(!guideOpen); setCatOpen(false); }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${navTextClass} ${navBgClass}`}
              >
                <i className="ri-book-open-line mr-1"></i>
                Guia de Compras
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideOpen ? "rotate-180" : ""}`}></i>
              </button>
              {guideOpen && (
                <div className={`absolute top-full left-0 mt-1 w-72 rounded-xl border shadow-lg py-2 z-50 ${
                  scrolled || !isHomePage
                    ? "bg-background-50 border-background-200/70"
                    : "bg-background-50/95 backdrop-blur-md border-background-200/50"
                }`}>
                  {articlesList.slice(0, 6).map((article) => (
                    <a
                      key={article.slug}
                      href={`/artigo/${article.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setGuideOpen(false)}
                    >
                      <span className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-article-line text-accent-600 text-xs"></i>
                      </span>
                      <span className="truncate">{article.title}</span>
                    </a>
                  ))}
                  <div className="border-t border-background-200/70 mt-1 pt-1">
                    <a
                      href="/artigo/guia-completo-hoobuy-spreadsheet"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-primary-600 hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setGuideOpen(false)}
                    >
                      <i className="ri-arrow-right-line"></i>
                      Ver Todos os Guias
                    </a>
                  </div>
                </div>
              )}
            </div>

            <a
              href="#sobre"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${navTextClass} ${navBgClass}`}
            >
              {t("nav.about")}
            </a>
            <a
              href="#faq"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${navTextClass} ${navBgClass}`}
            >
              {t("nav.faq")}
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                isLight
                  ? "text-foreground-950 bg-background-50 hover:bg-background-100"
                  : "text-background-50 bg-primary-500 hover:bg-primary-600"
              }`}
            >
              Hoobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                isLight
                  ? "text-background-50 border-2 border-background-50/70 hover:bg-background-50 hover:text-foreground-950"
                  : "text-foreground-950 border-2 border-foreground-300 hover:bg-foreground-950 hover:text-background-50"
              }`}
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
            aria-label="Menu"
          >
            <i
              className={`text-xl transition-colors ${isLight ? "text-background-50" : "text-foreground-950"} ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}
            ></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col max-h-[70vh] overflow-y-auto">
          <a
            href="/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            {t("nav.home")}
          </a>

          <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider mt-2">
            {t("nav.categories")}
          </div>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-2.5 text-xs font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-6 h-6 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${getCategoryIcon(cat.name)} text-primary-600 text-[10px]`}></i>
                </span>
                {cat.namePt}
              </a>
            ))}
          </div>

          <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider mt-3">
            Guia de Compras
          </div>
          {articlesList.slice(0, 4).map((article) => (
            <a
              key={article.slug}
              href={`/artigo/${article.slug}`}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-article-line text-accent-600"></i>
              <span className="truncate">{article.title}</span>
            </a>
          ))}

          <div className="border-t border-background-200/70 mt-3 pt-3 flex flex-col gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="px-4 py-3 text-sm font-semibold text-center text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all whitespace-nowrap cursor-pointer"
            >
              Hoobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="px-4 py-3 text-sm font-semibold text-center text-foreground-950 border-2 border-foreground-300 rounded-full hover:bg-foreground-950 hover:text-background-50 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

function getCategoryIcon(name: string): string {
  const map: Record<string, string> = {
    "Shoes": "ri-footprint-line",
    "Hoodies/Sweaters": "ri-t-shirt-line",
    "T-Shirts": "ri-t-shirt-2-line",
    "Jackets": "ri-shirt-line",
    "Pants/Shorts": "ri-pantone-line",
    "Headwear": "ri-base-station-line",
    "Sets": "ri-stack-line",
    "Underwear/Underpants": "ri-checkbox-blank-line",
    "Jersey": "ri-trophy-line",
    "Accessories": "ri-glasses-line",
    "Bags": "ri-briefcase-line",
    "Belts": "ri-link",
    "Perfumes": "ri-drop-line",
    "Other Stuff": "ri-more-line",
  };
  return map[name] || "ri-arrow-right-line";
}